import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { getImportLog } from "../../store/importHistoryReducer";
import { useSelector } from "react-redux";
import useImportColumns from "./hooks/useImportColumns";
import { Box, Card, CircularProgress, Grid, InputAdornment, TextField, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import IncidentsTable from "../../components/Incidents/IncidentsTable";
import ImportXml from "../Incidents/ImportXml";
import { debounce } from "lodash";
import ErrorDialog from "../../components/Incidents/modals/ErrorDialog";

const ImportHistory = () => {
  const dispatch = useDispatch();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [searchQuery, setSearchQuery] = useState("");
  const [openErrorDialogData, setOpenErrorDialogData] = useState({ open: false, errors: null });

  let { importLogList, loading } = useSelector((state) => state.importHistoryReducer);

  const requestForImports = (page, query, pageSize) => {
    dispatch(getImportLog(page, query, pageSize));
  };

  useEffect(() => {
    requestForImports(paginationModel.page + 1, searchQuery, paginationModel.pageSize);
  }, [paginationModel]);

  const columns = useImportColumns(
    () => requestForImports(paginationModel.page + 1, searchQuery, paginationModel.pageSize),
    setOpenErrorDialogData
  );

  const handleSearch = (query) => {
    if (query.length > 0) {
      requestForImports(1, query, paginationModel.pageSize);
    } else {
      requestForImports(1, null, paginationModel.pageSize);
    }
  };

  const debouncedHandlerSearch = useMemo(() => debounce((query) => handleSearch(query), 1000), [paginationModel]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedHandlerSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
    requestForImports(1, null, paginationModel.pageSize);
  };

  const onPaginationModelChange = (e) => {
    setPaginationModel(e);
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialogData({ open: false, errors: null });
  };

  return (
    <>
      <Box>
        <Card
          className="p-3 mt-4"
          sx={{
            boxShadow: "0px 4px 4px 0px #0000000F",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Import History
            </Typography>
            <Box className="d-flex">
              <ImportXml incidentsRequest={clearSearch} />
            </Box>
          </Box>

          <Grid container spacing={2} className="my-2">
            <Grid item xs={12} md={4}>
              <TextField
                color={"secondary"}
                label="Search incidents"
                placeholder="Search by incident number, type, or location..."
                size="small"
                value={searchQuery}
                onChange={handleSearchChange}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchQuery.length > 0 ? (
                        <ClearIcon onClick={clearSearch} sx={{ cursor: "pointer" }} />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* <Grid item xs={12} md={6} className="d-flex justify-content-end">
              <FilterIncidents />
            </Grid> */}
            <Grid item xs={12}>
              {loading ? (
                <Box
                  sx={{ minHeight: "100px" }}
                  className="w-100 h-100 d-flex justify-content-center align-items-center"
                >
                  <CircularProgress />
                </Box>
              ) : !loading && (!importLogList?.data || importLogList?.data.length === 0) ? (
                <Typography className="mt-2" color="#6E7C87">
                  No imports found matching your search.
                </Typography>
              ) : (
                <IncidentsTable
                  rows={importLogList?.data}
                  columns={columns}
                  onPaginationModelChange={onPaginationModelChange}
                  paginationModel={paginationModel}
                  rowsCount={importLogList?.total}
                />
              )}
            </Grid>
          </Grid>
        </Card>
      </Box>
      <ErrorDialog
        open={openErrorDialogData.open}
        onClose={handleCloseErrorDialog}
        errors={openErrorDialogData.errors}
      />
    </>
  );
};

export default ImportHistory;
